// src/utils/dentallyLeadSyncPresentation.js
//
// Plain-English wording for the per-lead Dentally sync panel. The sentence
// shapes match the review wizard's `describeDifference` / `fieldUpdateLabel`
// (src/pages/dashboard/tables/dentally/wizard/reviewPresentation.js) so a member
// of staff reads the same thing in both places.
//
// The labels are intentionally NOT imported from the wizard: on a patient's own
// record the phone field is read as "Mobile number", not "Phone". Backend labels
// are accepted as a fallback for fields added later, but a known field always
// uses the wording agreed here.

export const FIELD_LABEL = Object.freeze({
  first_name: 'First name',
  last_name: 'Last name',
  dob: 'Date of birth',
  date_of_birth: 'Date of birth',
  email: 'Email',
  phone: 'Mobile number',
  mobile: 'Mobile number',
  mobile_phone: 'Mobile number',
  address: 'Address',
});

const humanise = (field) => String(field || '').replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());

/** Preferred label for a difference: agreed wording first, then the backend's, then the slug. */
export const differenceLabel = (diff) => FIELD_LABEL[diff?.field]
  || (typeof diff?.label === 'string' && diff.label.trim() ? diff.label.trim() : '')
  || humanise(diff?.field);

export const hasValue = (value) => value !== null && value !== undefined && String(value).trim() !== '';

export const formatDate = (value) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? String(value)
    : date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

export const formatValue = (field, value) => {
  if (!hasValue(value)) return '';
  if (field === 'dob' || field === 'date_of_birth') return formatDate(value);
  return String(value);
};

/** "Email — CRM: a@b.com · Dentally: c@d.com", or "CRM has no email · Dentally: c@d.com". */
export const describeDifference = (diff) => {
  const label = differenceLabel(diff);
  const dentally = formatValue(diff?.field, diff?.dentally_value);
  if (!hasValue(diff?.crm_value)) return `CRM has no ${label.toLowerCase()} · Dentally: ${dentally}`;
  return `${label} — CRM: ${formatValue(diff?.field, diff.crm_value)} · Dentally: ${dentally}`;
};

/** Checkbox description for screen readers, e.g. "Use the Dentally email". */
export const dentallyValueAriaLabel = (diff) => `Use the Dentally ${differenceLabel(diff).toLowerCase()}`;

/** "email and date of birth" / "email, mobile number and address". */
export const joinLabels = (labels) => {
  const parts = labels.filter(Boolean);
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0];
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
};

/** Differences the panel can act on: something to copy over from Dentally. */
export const usableDifferences = (differences) => (Array.isArray(differences) ? differences : [])
  .filter((diff) => diff && typeof diff === 'object' && diff.field && hasValue(diff.dentally_value));

/** "Last checked at 14:32 today" style suffix for the quiet "everything matches" line. */
export const formatCheckedAt = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const now = new Date();
  const sameDay = date.toDateString() === now.toDateString();
  const time = date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
  return sameDay ? `Checked at ${time} today` : `Checked on ${formatDate(value)} at ${time}`;
};

/**
 * Plain reason for a field the backend would not copy. These are the three codes
 * in the fixed contract; an unrecognised code falls through to the server's own
 * words, and a bare unknown token is dropped rather than shown to staff.
 */
export const SKIPPED_REASON = Object.freeze({
  not_in_differences: 'it already matches',
  too_long: 'the Dentally value is too long to store',
  invalid: 'the Dentally value could not be read',
});

export const describeSkipped = (skipped) => {
  const label = differenceLabel(skipped);
  const code = skipped?.reason;
  const known = SKIPPED_REASON[code];
  const plain = known || (typeof code === 'string' && code.includes(' ') ? code : '');
  return plain ? `${label} was not changed because ${plain}.` : `${label} was not changed.`;
};

/**
 * Why the apply controls are not offered, reported by GET as `blocked_reason`
 * alongside `can_apply: false`. Staff see the differences read-only with one of
 * these lines, rather than a disabled button with no explanation.
 */
export const BLOCKED_REASON_MESSAGE = Object.freeze({
  lead_archived: 'This lead is archived, so these details can’t be updated.',
  permission: 'You can see these differences but not change them.',
  not_linked: 'This lead is no longer linked to a Dentally patient, so these details can’t be updated.',
  integration_inactive: 'Dentally is not active for this practice just now, so these details can’t be updated.',
});

/** Falls back to the neutral view-only line for an unrecognised or absent reason. */
export const describeBlockedReason = (reason) => BLOCKED_REASON_MESSAGE[reason]
  || BLOCKED_REASON_MESSAGE.permission;

/**
 * Why an apply changed nothing at all. Codes come from the POST error responses
 * in the fixed contract. Anything else gets the neutral "try again" sentence,
 * which is true of every remaining case (the server writes nothing on failure).
 */
export const APPLY_FAILURE_MESSAGE = Object.freeze({
  lead_archived: 'This lead is archived, so nothing was changed.',
  not_linked: 'This lead is no longer linked to a Dentally patient, so nothing was changed.',
  integration_unavailable: 'Dentally is not available for this practice just now, so nothing was changed.',
  forbidden: 'You no longer have permission to change this lead, so nothing was changed.',
  provider_unavailable: 'Dentally could not be reached, so nothing was changed. Please try again in a moment.',
});

export const describeApplyFailure = (code) => APPLY_FAILURE_MESSAGE[code]
  || 'Nothing was changed. Please try again in a moment.';
